import Track from 'core/logger/track';
import { getLabelForPremiumPlanCode } from 'core/utils';
import FireStoreClass from '@FireStore';
import { getKlaviyoInstance } from 'core/libs/klaviyo';
import { GenericObject } from 'core/models/platform';
import {
  SYSTEM_SUBSCRIPTION_RESUBSCRIBE,
  SYSTEM_SUBSCRIPTION_RESUME,
  SYSTEM_SUBSCRIPTION_UPDATED_BILLING_DETAILS,
  SYSTEM_SUBSCRIPTION_UPDATED_PAYMENT_METHOD,
  SYSTEM_SUBSCRIPTION_UPGRADE,
  USER_CLICKED_FULL_REFUND,
  USER_CLICKED_INVOICE_DOWNLOAD,
  USER_CLICKED_PAUSE,
  USER_CLICKED_RESUBSCRIBE,
  USER_CLICKED_RESUME,
  USER_CLICKED_UNSUBSCRIBE_REFUND_OPTION,
  USER_CLICKED_UPGRADE,
  USER_CLICKED_UPGRADING_PLAN,
} from '../models/names';

let Firestore: FireStoreClass;
if (FireStoreClass) {
  Firestore = FireStoreClass.getInstance();
}

class TrackPayments {
  constructor() {}
  subscribeLinkClicked = (plan: number, position: string) => {
    const planLabels: { [key: number]: string } = {
      0: 'free',
      1: 'monthly',
      2: 'semiAnnual',
      3: 'annual',
    };
    const planName = planLabels[plan] || 'no';
    Track.getInstance().gaEvent('subscribeLinks', planName + 'PlanClicked', position);
    Track.getInstance().gTagEvent('subscribeLinkClicked', { position, plan: planName });
    Track.getInstance().ampEvent('subscribeLinkClicked', { position, plan: planName });
  };

  upgradeLinkClicked = (position: string) => {
    Track.getInstance().upgradeLinkClicked(position);
  };

  viewPlansClicked = (position: string) => {
    Track.getInstance().gaEvent('subscribeLinks', 'viewPlansClicked', position);
    Track.getInstance().gTagEvent('viewPlansClicked', { position });
    Track.getInstance().ampEvent('viewPlansClicked', { position });
  };

  selectPlan = (plan: number, price: number) => {
    Track.getInstance().storeActionEvent('selectPlan', { plan: plan });
    let data = {
      planCode: plan,
      planName: getLabelForPremiumPlanCode(plan),
      planValue: price,
    };
    Track.getInstance().ampEvent('selectPlan', data);
    Track.getInstance().gTagEvent('selectPlan', data);
  };

  unverifiedSelectPlan = (plan: number, price: number) => {
    Track.getInstance().storeActionEvent('unverifiedSelectPlan', { plan: plan });
    let data = {
      planCode: plan,
      planName: getLabelForPremiumPlanCode(plan),
      planValue: price,
    };
    Track.getInstance().ampEvent('unverifiedSelectPlan', data);
    Track.getInstance().gTagEvent('unverifiedSelectPlan', data);
  };

  openedCheckout = (
    paymentMethod: 'Stripe' | 'Paypal',
    plan: number,
    planLabel: string,
    dollarValue: number
  ) => {
    const data = { paymentMethod, plan, planLabel, dollarValue };
    Track.getInstance().storeActionEvent('cart-creditcard', data);
    Track.getInstance().gaEvent('purchase', 'openedCheckout');
    // @TODO: check below ga event is getting fired
    if (window && window.ga) window.ga('set', 'metric7', '1');
    Track.getInstance().gaEvent('ec:setAction', 'checkout', { step: 2 });
    Track.getInstance().ampEvent('openedCheckout', data);
  };

  paymentError = (error: unknown, provider?: string) => {
    const data = {
      paymentMethod: provider ? provider : null,
    };
    Track.getInstance().storeActionEvent('paymentError', { errorMsg: error, ...data });
    Track.getInstance().ampEvent('paymentError', { errorMsg: error, ...data });
  };

  addedCoupon = (plan: number, value: number, coupon: string, discountValue?: number) => {
    if (discountValue) {
      Track.getInstance().ampEvent('add-coupon', { coupon, plan, value, discountValue });
      Track.getInstance().storeActionEvent('add-coupon', {
        coupon,
        plan,
        value,
        discountValue,
      });
    }
  };

  removedCoupon = (plan: number, value: number, coupon: string, discountValue?: number) => {
    if (discountValue) {
      Track.getInstance().ampEvent('remove-coupon', { coupon, plan, value, discountValue });
      Track.getInstance().storeActionEvent('remove-coupon', {
        coupon,
        plan,
        value,
        discountValue,
      });
    }
  };

  boughtPremium = (
    plan: number,
    value: number,
    coupon?: string,
    discountValue?: number,
    discountType?: string
  ) => {
    Track.getInstance().storeActionEvent('cart-bought', {
      plan,
      value,
      coupon,
      discountValue: (discountValue || 0) / 100,
      discountType,
    });
    Track.getInstance().gaEvent('purchase', 'boughtPremium', plan);
    if (window && window.ga) window.ga('set', 'metric9', '9.95');
    Track.getInstance().gaEvent('ec:setAction', 'purchase', {
      id: Firestore.getUsername(),
      affiliation: plan,
      revenue: value,
      tax: '0',
      shipping: '0', // User added a coupon at checkout.
    });
    Track.getInstance().gaEvent('ec:setAction', 'checkout', { step: 4 });
    const data = {
      affiliation: getLabelForPremiumPlanCode(plan),
      planCode: plan,
      revenue: value,
      $value: value,
      currency: '$',
    };
    getKlaviyoInstance().track('boughtPremium', data);
    // Track.getInstance().ampEvent('boughtPremium', data);
    Track.getInstance().gTagEvent('boughtPremium', data);
  };

  submittedCard = (provider: string) => {
    const data = {
      paymentMethod: provider,
    };
    Track.getInstance().storeActionEvent('cart-pay');
    Track.getInstance().gaEvent('purchase', 'submittedCard');
    if (window && window.ga) window.ga('set', 'metric8', '1');
    Track.getInstance().gaEvent('ec:setAction', 'checkout', { step: 3 });
    Track.getInstance().ampEvent('submittedCard', data);
    Track.getInstance().gTagEvent('submittedCard', data);
  };

  cardAlreadyExists = (provider: string) => {
    const data = {
      paymentMethod: provider,
    };
    Track.getInstance().storeActionEvent('cardAlreadyExists', data);
    Track.getInstance().ampEvent('cardAlreadyExists', data);
    Track.getInstance().gTagEvent('cardAlreadyExists', data);
  };

  cardRuleHit = (data: { paymentMethod: string; code: string; message: string }) => {
    Track.getInstance().storeActionEvent('cardRuleHit', data);
    Track.getInstance().ampEvent('cardRuleHit', data);
    Track.getInstance().gTagEvent('cardRuleHit', data);
  };

  processingCard = (data: {
    paymentMethod: string;
    paymentIntentStatus?: string;
    step?: string;
  }) => {
    Track.getInstance().storeActionEvent('processingCard', data);
    Track.getInstance().ampEvent('processingCard', data);
    Track.getInstance().gTagEvent('processingCard', data);
  };

  checkoutModalAction = (action: string) => {
    Track.getInstance().storeActionEvent('cart-pay');
    Track.getInstance().gaEvent('purchase', action);
    if (window && window.ga) window.ga('set', 'metric8', '1');
    Track.getInstance().gaEvent('ec:setAction', 'checkout', { step: 3 });
    Track.getInstance().ampEvent(action);
    Track.getInstance().gTagEvent(action);
  };

  updatedPaymentMethod = (data: GenericObject) => {
    Track.getInstance().storeActionEvent(SYSTEM_SUBSCRIPTION_UPDATED_PAYMENT_METHOD, data);
    Track.getInstance().ampEvent(SYSTEM_SUBSCRIPTION_UPDATED_PAYMENT_METHOD, data);
  };

  unsubscribeSubscriptionButtonClick = () => {
    Track.getInstance().storeActionEvent('userClickedOnUnsubscribePremiumButton');
    Track.getInstance().ampEvent('userClickedOnUnsubscribePremiumButton');
  };

  resubscribeSubscriptionButtonClick = () => {
    Track.getInstance().storeActionEvent(USER_CLICKED_RESUBSCRIBE);
    Track.getInstance().ampEvent(USER_CLICKED_RESUBSCRIBE);
  };

  pauseSubscriptionButtonClick = (data: GenericObject) => {
    Track.getInstance().storeActionEvent(USER_CLICKED_PAUSE, data);
    Track.getInstance().ampEvent(USER_CLICKED_PAUSE, data);
  };

  resumeSubscriptionButtonClick = () => {
    Track.getInstance().storeActionEvent(USER_CLICKED_RESUME);
    Track.getInstance().ampEvent(USER_CLICKED_RESUME);
  };

  upgradeSubscriptionButtonClick = (data: GenericObject) => {
    Track.getInstance().storeActionEvent(USER_CLICKED_UPGRADE, data);
    Track.getInstance().ampEvent(USER_CLICKED_UPGRADE, data);
  };

  upgradingToButtonClick = (data: GenericObject) => {
    Track.getInstance().storeActionEvent(USER_CLICKED_UPGRADING_PLAN, data);
    Track.getInstance().ampEvent(USER_CLICKED_UPGRADING_PLAN, data);
  };

  upgradeSubscription = (data: GenericObject) => {
    Track.getInstance().storeActionEvent(SYSTEM_SUBSCRIPTION_UPGRADE, data);
    Track.getInstance().ampEvent(SYSTEM_SUBSCRIPTION_UPGRADE, data);
  };

  pauseSubscription = (feedbackObj: GenericObject) => {
    Track.getInstance().storeActionEvent('pauseFeedback', feedbackObj);
    Track.getInstance().ampEvent('pauseFeedback', feedbackObj);
  };

  resumeSubscription = () => {
    Track.getInstance().storeActionEvent(SYSTEM_SUBSCRIPTION_RESUME);
    Track.getInstance().ampEvent(SYSTEM_SUBSCRIPTION_RESUME);
  };

  unsubscribeSubscription = (feedbackObj: GenericObject) => {
    Track.getInstance().storeActionEvent('unsubscribeFeedback', feedbackObj);
    Track.getInstance().ampEvent('unsubscribeFeedback', feedbackObj);
  };

  clickedUnsubscribeRefundOption = (data: GenericObject) => {
    Track.getInstance().storeActionEvent(USER_CLICKED_UNSUBSCRIBE_REFUND_OPTION, data);
    Track.getInstance().ampEvent(USER_CLICKED_UNSUBSCRIBE_REFUND_OPTION, data);
  };

  resubscribeSubscription = () => {
    Track.getInstance().storeActionEvent(SYSTEM_SUBSCRIPTION_RESUBSCRIBE);
    Track.getInstance().ampEvent(SYSTEM_SUBSCRIPTION_RESUBSCRIBE);
  };

  updatedBillingAddress = (fields: string[]) => {
    Track.getInstance().storeActionEvent(SYSTEM_SUBSCRIPTION_UPDATED_BILLING_DETAILS, fields);
    Track.getInstance().ampEvent(SYSTEM_SUBSCRIPTION_UPDATED_BILLING_DETAILS, fields);
  };

  downloadedInvoice = () => {
    Track.getInstance().storeActionEvent(USER_CLICKED_INVOICE_DOWNLOAD);
    Track.getInstance().ampEvent(USER_CLICKED_INVOICE_DOWNLOAD);
  };
}
// @TODO: expose instance within the class
const paymentsTrack = new TrackPayments();
export default paymentsTrack;
